/**
 * Buyer verification — the signed-in buyer's self-service KYC/FICA state +
 * marketplace transact-eligibility (web).
 *
 * Web mirror of apps/mobile/app/buyer-verification.tsx, and structurally the
 * twin of the farmer KYC screen apps/web/src/routes/_app.verification.tsx: it
 * loads the caller's KYC via GET /kyc/me (orval) and lets them register a Tier-1
 * verification intent via POST /kyc {ficaTier:1}. Tier 1 is the only tier that
 * does anything useful pre-partner-integration — the backend stub bumps
 * ficaStatus to 'submitted' and records the tier; document upload + liveness
 * (Tier 2, Smile ID / D-011) land later. This screen captures INTENT only.
 *
 * Layered on top: GET /buyers/me/eligibility → { ficaStatus, canTransact }. A
 * buyer may NOT place an offer until verified, so the top of the screen shows a
 * can-transact banner derived from that endpoint, and the verification journey
 * makes "awaiting approval" explicit after submit. Reached from a 403
 * buyer_kyc_required prompt on the listing-detail offer flow (the offer screen
 * links here), or from the buyer nav.
 *
 * Contract: src/api/buyers.ts (getBuyerEligibility / submitBuyerKyc / getMyKyc,
 * thin wrappers over the generated /kyc client). i18n keys live under
 * buyerVerification.* (+ the shared verification.status.* labels) in all four
 * web locales. The nav item is role-gated to `buyer` in _app.tsx (central wiring).
 */
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  CircularProgress,
  Divider,
  ListItem,
  Stack,
  Stepper,
  StatusPill,
  Text,
  type StatusPillVariant,
} from '@fse/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import { FseApiError } from '../api/client';
import { getBuyerEligibility, getMyKyc, submitBuyerKyc, type KycDto } from '../api/buyers';
import type { KycDtoFicaStatus } from '../api/generated/models';

const KYC_QUERY_KEY = ['my-kyc'];
const ELIGIBILITY_QUERY_KEY = ['buyer-eligibility'];

/** StatusPill presentation per FICA status (keyed by the generated FICA type).
 *  Mirrors _app.verification.tsx so the two read identically. */
const STATUS_PILL: Record<
  KycDtoFicaStatus,
  { labelKey: string; variant: StatusPillVariant; icon: string }
> = {
  none: { labelKey: 'verification.status.none', variant: 'upcoming', icon: 'radio_button_unchecked' },
  submitted: { labelKey: 'verification.status.submitted', variant: 'due', icon: 'schedule' },
  verified: { labelKey: 'verification.status.verified', variant: 'active', icon: 'check_circle' },
  rejected: { labelKey: 'verification.status.rejected', variant: 'overdue', icon: 'error' },
  expired: { labelKey: 'verification.status.expired', variant: 'due', icon: 'history' },
};

/** Synthetic "not started" state — used when no KYC row exists / GET 404s. */
const SYNTHETIC_NONE: KycDto = {
  userId: '',
  ficaStatus: 'none',
  ficaTier: null,
  livenessRef: null,
  docMinioKey: null,
  reviewedBy: null,
  reviewedAt: null,
  expiresAt: null,
};

function formatTs(iso: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString();
}

/** A label/value row used inside the status card, as a kit `ListItem`. */
function DetailRow({
  label,
  value,
  leadingIcon,
  noDivider = false,
}: {
  label: string;
  value: string;
  leadingIcon?: string;
  noDivider?: boolean;
}) {
  return (
    <ListItem
      leadingIcon={leadingIcon}
      title={
        <Text variant="body-medium" color="text.secondary">
          {label}
        </Text>
      }
      trailing={
        <Box sx={{ textAlign: 'right' }}>
          <Text variant="body-medium" color="text.primary">
            <Box component="span" sx={{ fontWeight: 500 }}>
              {value}
            </Box>
          </Text>
        </Box>
      }
      noDivider={noDivider}
    />
  );
}

export function BuyerVerificationScreen() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const kycQuery = useQuery({
    queryKey: KYC_QUERY_KEY,
    // GET /kyc/me shouldn't 404 for a signed-in user, but if it does we treat
    // it as a clean "not started" state rather than an error page.
    queryFn: async ({ signal }): Promise<KycDto> => {
      try {
        return await getMyKyc(signal);
      } catch (err) {
        if (err instanceof FseApiError && err.status === 404) {
          return SYNTHETIC_NONE;
        }
        throw err;
      }
    },
    retry: false,
  });

  // Marketplace transact-eligibility — drives the can-transact banner. Best
  // effort: a failure here must not block the KYC submit flow, so it's a soft
  // signal (we just hide the banner if it errors / is still loading).
  const eligibilityQuery = useQuery({
    queryKey: ELIGIBILITY_QUERY_KEY,
    queryFn: ({ signal }) => getBuyerEligibility(signal),
    retry: false,
  });

  const submitMutation = useMutation({
    mutationFn: () => submitBuyerKyc(),
    onSuccess: (updated: KycDto) => {
      // Prime the cache so the status card reflects the new 'submitted' state,
      // then refetch eligibility (canTransact may flip server-side).
      queryClient.setQueryData(KYC_QUERY_KEY, updated);
      queryClient.invalidateQueries({ queryKey: ELIGIBILITY_QUERY_KEY });
    },
  });

  const kyc = kycQuery.data;
  const status = kyc?.ficaStatus ?? 'none';
  const alreadySubmitted = status === 'submitted';
  const alreadyVerified = status === 'verified';
  const submitDisabled = alreadySubmitted || alreadyVerified || submitMutation.isPending;

  // Active step in the three-stage journey (none → submitted → verified).
  // rejected / expired reset the visual journey to the start.
  const journeyStep = alreadyVerified ? 2 : alreadySubmitted ? 1 : 0;

  const canTransact = eligibilityQuery.data?.canTransact ?? false;

  return (
    <Stack spacing={3} sx={{ maxWidth: 640 }}>
      <Box>
        <Text variant="headline-small" as="h2">
          {t('buyerVerification.title')}
        </Text>
        <Box sx={{ mt: 0.5 }}>
          <Text variant="body-medium" color="text.secondary">
            {t('buyerVerification.subtitle')}
          </Text>
        </Box>
      </Box>

      {/* Transact-eligibility banner — only when we have a definite answer. */}
      {eligibilityQuery.data ? (
        <Alert severity={canTransact ? 'success' : 'info'}>
          {canTransact
            ? t('buyerVerification.canTransact')
            : t('buyerVerification.cannotTransact')}
        </Alert>
      ) : null}

      {kycQuery.isPending ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      ) : kycQuery.isError || !kyc ? (
        <Alert
          severity="error"
          action={
            <Button color="inherit" size="small" onClick={() => kycQuery.refetch()}>
              {t('buyerVerification.retry')}
            </Button>
          }
        >
          {t('buyerVerification.loadError', { error: String(kycQuery.error) })}
        </Alert>
      ) : (
        <>
          {/* Status card */}
          <Card>
            <CardHeader title={t('buyerVerification.statusCardTitle')} />
            <CardContent>
              <Stack spacing={2}>
                <Box>
                  <StatusPill variant={STATUS_PILL[status].variant} icon={STATUS_PILL[status].icon}>
                    {t(STATUS_PILL[status].labelKey)}
                  </StatusPill>
                </Box>

                {/* Verification journey — reuses the three status labels as the
                    step names (shared verification.status.* keys, no new copy). */}
                <Box sx={{ pt: 1 }}>
                  <Stepper
                    steps={[
                      t('verification.status.none'),
                      t('verification.status.submitted'),
                      t('verification.status.verified'),
                    ]}
                    activeStep={journeyStep}
                  />
                </Box>

                <Divider />
                <Box
                  sx={{
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 2,
                    overflow: 'hidden',
                  }}
                >
                  <DetailRow
                    leadingIcon="workspace_premium"
                    label={t('buyerVerification.tier')}
                    value={kyc.ficaTier != null ? String(kyc.ficaTier) : '—'}
                  />
                  <DetailRow
                    leadingIcon="fact_check"
                    label={t('buyerVerification.reviewedAt')}
                    value={formatTs(kyc.reviewedAt)}
                  />
                  <DetailRow
                    leadingIcon="event_busy"
                    label={t('buyerVerification.expiresAt')}
                    value={formatTs(kyc.expiresAt)}
                    noDivider
                  />
                </Box>
              </Stack>
            </CardContent>
          </Card>

          {/* Submit / intent card */}
          <Card>
            <CardHeader title={t('buyerVerification.getVerified')} />
            <CardContent>
              <Stack spacing={2}>
                <Text variant="body-medium" color="text.secondary">
                  {t('buyerVerification.intentBody')}
                </Text>

                {submitMutation.isError ? (
                  <Alert severity="error">
                    {t('buyerVerification.submitError', {
                      error:
                        submitMutation.error instanceof FseApiError
                          ? submitMutation.error.message
                          : String(submitMutation.error),
                    })}
                  </Alert>
                ) : null}

                {alreadyVerified ? (
                  <Alert severity="success">{t('buyerVerification.alreadyVerified')}</Alert>
                ) : alreadySubmitted ? (
                  <Alert severity="info">{t('buyerVerification.awaitingApproval')}</Alert>
                ) : (
                  <Box>
                    <Button
                      variant="contained"
                      onClick={() => submitMutation.mutate()}
                      disabled={submitDisabled}
                      startIcon={
                        submitMutation.isPending ? <CircularProgress size={16} /> : undefined
                      }
                    >
                      {submitMutation.isPending
                        ? t('buyerVerification.submitting')
                        : t('buyerVerification.submitButton')}
                    </Button>
                  </Box>
                )}

                <Text variant="body-small" color="text.secondary">
                  {t('buyerVerification.tierNote')}
                </Text>
              </Stack>
            </CardContent>
          </Card>
        </>
      )}
    </Stack>
  );
}
